import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { ArrowLeft, Plus, Edit2, Trash2, Eye, EyeOff, Upload, X, Loader2, ImagePlus, GripVertical, Download, FileSpreadsheet, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Toast } from '../components/ui/Toast';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import type { MenuApi, MenuSection, FoodProduct, SaveMenuSection, ProductAddonInput } from '../services/resortAdminApi';

const ECT_DEFAULT = 10;
const MAX_TAGS = 3;
const MAX_TAGS_MESSAGE = `Maximum of ${MAX_TAGS} tags can be assigned to a product.`;
// Mandatory CSV columns, mirrored from the kitchen-app importer.
const CSV_REQUIRED_COLUMNS = ['food_name', 'food_description', 'ingredients', 'price', 'menu_section_id'];

interface ProductForm {
  food_name: string;
  price: string;
  food_description: string;
  ingredients: string;
  allergens: string;
  estimated_completion_time: string;
  status: 'Active' | 'Hidden';
  food_tags: string[];
}

const emptyProductForm = (): ProductForm => ({
  food_name: '',
  price: '',
  food_description: '',
  ingredients: '',
  allergens: '',
  estimated_completion_time: String(ECT_DEFAULT),
  status: 'Active',
  food_tags: [],
});

// Local editing shape for product add-ons. Prices are kept as strings while
// in the inputs (blank → 0 on save; price 0 hydrates as '' so the "€0.00"
// placeholder shows for free options).
interface AddonOptionForm {
  name: string;
  price: string;
}
interface AddonForm {
  id?: string; // present for groups that already exist on the backend
  title: string;
  selection_type: 'single' | 'multi';
  required: boolean;
  options: AddonOptionForm[];
}

const emptyAddon = (): AddonForm => ({
  title: '',
  selection_type: 'single',
  required: false,
  options: [{ name: '', price: '' }],
});

// Editable shape for the photo-import review step.
interface ReviewProduct {
  food_name: string;
  ingredients: string;
  price: string;
}
interface ReviewSection {
  section_name: string;
  existing_section: string; // '' = create new section
  products: ReviewProduct[];
}

interface MenuEditorProps {
  menuApi: MenuApi;
}

export const MenuEditor: React.FC<MenuEditorProps> = ({ menuApi }) => {
  const { name } = useParams<{ name: string }>();
  const rid = name || '';
  const navigate = useNavigate();
  const { isReadOnly } = useAuth();

  const [entityName, setEntityName] = useState('');
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [products, setProducts] = useState<FoodProduct[]>([]);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [busy, setBusy] = useState(false);

  // Section modal
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<MenuSection | null>(null);
  const [sectionName, setSectionName] = useState('');

  // Product modal
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<FoodProduct | null>(null);
  const [productForm, setProductForm] = useState<ProductForm>(emptyProductForm());
  const [linkProductName, setLinkProductName] = useState<string | null>(null);
  // Product add-ons (option groups). Only one addon card is expanded at a time.
  const [productAddons, setProductAddons] = useState<AddonForm[]>([]);
  const [expandedAddon, setExpandedAddon] = useState<number | null>(null);
  // Product image (remote /files url, or a local blob: preview for a not-yet-saved product)
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [productImageUrl, setProductImageUrl] = useState<string | null>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Delete confirm
  const [confirm, setConfirm] = useState<{ kind: 'section' | 'product'; id: string; label: string } | null>(null);
  // "This product has orders — hide instead?" follow-up
  const [orderedProduct, setOrderedProduct] = useState<{ id: string; label: string } | null>(null);

  // Drag & drop reorder (index-based, like the kitchen-app)
  const [dragSection, setDragSection] = useState<number | null>(null);
  const [dragOverSection, setDragOverSection] = useState<number | null>(null);
  const [dragProduct, setDragProduct] = useState<number | null>(null);
  const [dragOverProduct, setDragOverProduct] = useState<number | null>(null);
  // Separate drag states for the addon cards list and for options inside one
  // addon, so dragging an option never triggers an addon reorder.
  const [dragAddon, setDragAddon] = useState<number | null>(null);
  const [dragOverAddon, setDragOverAddon] = useState<number | null>(null);
  const [dragOption, setDragOption] = useState<{ addon: number; index: number } | null>(null);
  const [dragOverOption, setDragOverOption] = useState<{ addon: number; index: number } | null>(null);

  // AI import flow (restaurants only)
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [reviewSections, setReviewSections] = useState<ReviewSection[] | null>(null);
  const [savingImport, setSavingImport] = useState(false);

  // CSV / Excel import
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [csvBusy, setCsvBusy] = useState(false);

  const showError = (e: any) =>
    setToast({ message: e?.response?.data?.message || e?.message || 'Something went wrong', type: 'error' });

  const getImageUrl = (url: string | null): string => {
    if (!url) return '';
    if (/^(https?:|blob:|data:)/.test(url)) return url;
    const apiBase = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) || window.location.origin;
    return url.startsWith('/') ? `${apiBase}${url}` : `${apiBase}/files/${url}`;
  };

  const loadMenu = async (keepSelection = true) => {
    const [secs, prods] = await Promise.all([
      menuApi.listSections(rid),
      menuApi.listProducts(rid, true),
    ]);
    setSections(secs);
    setProducts(prods);
    setSelectedSection((prev) => {
      const stillValid = keepSelection && prev && secs.some((s) => s.name === prev);
      return stillValid ? prev : (secs[0]?.name ?? null);
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [displayName, tags] = await Promise.all([
          menuApi.getEntityName(rid).catch(() => rid),
          menuApi.listTags().catch(() => []),
        ]);
        if (cancelled) return;
        setEntityName(displayName);
        setAvailableTags(Array.isArray(tags) ? tags : []);
        await loadMenu(false);
      } catch (e) {
        if (!cancelled) showError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid, menuApi]);

  const sectionProducts = useMemo(
    () => products.filter((p) => p.menu_section === selectedSection),
    [products, selectedSection]
  );
  const productCount = useMemo(() => {
    const counts: Record<string, number> = {};
    products.forEach((p) => {
      counts[p.menu_section] = (counts[p.menu_section] || 0) + 1;
    });
    return counts;
  }, [products]);

  const tagOptions = availableTags.map((t) => ({ value: t, label: t }));
  // Products in other sections that can be linked into the current one.
  const linkableOptions = useMemo(
    () =>
      products
        .filter((p) => p.menu_section !== selectedSection)
        .map((p) => ({ value: p.name, label: p.food_name })),
    [products, selectedSection]
  );

  // ---- Section actions ----
  const openAddSection = () => {
    setEditingSection(null);
    setSectionName('');
    setSectionModalOpen(true);
  };
  const openEditSection = (s: MenuSection) => {
    setEditingSection(s);
    setSectionName(s.section_name);
    setSectionModalOpen(true);
  };
  const saveSection = async () => {
    const trimmed = sectionName.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      if (editingSection) {
        await menuApi.updateSection(rid, editingSection.name, trimmed);
      } else {
        const created = await menuApi.createSection(rid, trimmed);
        setSelectedSection(created.name);
      }
      setSectionModalOpen(false);
      await loadMenu();
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  // ---- Product actions ----
  const openAddProduct = () => {
    if (!selectedSection) return;
    setEditingProduct(null);
    setProductForm(emptyProductForm());
    setLinkProductName(null);
    setProductImageUrl(null);
    setPendingImageFile(null);
    setProductAddons([]);
    setExpandedAddon(null);
    setProductModalOpen(true);
  };
  const openEditProduct = (p: FoodProduct) => {
    setEditingProduct(p);
    setLinkProductName(null);
    setProductForm({
      food_name: p.food_name || '',
      price: p.price != null ? String(p.price) : '',
      food_description: p.food_description || '',
      ingredients: p.ingredients || '',
      allergens: p.allergens || '',
      estimated_completion_time: String(p.estimated_completion_time ?? ECT_DEFAULT),
      status: p.status === 'Hidden' ? 'Hidden' : 'Active',
      food_tags: p.food_tags || [],
    });
    setProductAddons(
      (p.addons ?? []).map((a) => ({
        id: a.id,
        title: a.title || '',
        selection_type: a.selection_type === 'multi' ? 'multi' : 'single',
        required: !!a.required,
        options: (a.options ?? []).map((o) => ({
          name: o.name || '',
          // price 0 (free) hydrates as '' so the input shows the €0.00 placeholder
          price: o.price ? String(o.price) : '',
        })),
      }))
    );
    setExpandedAddon(null);
    setProductImageUrl(p.image || null);
    setPendingImageFile(null);
    setProductModalOpen(true);
  };

  // ---- Add-on editing helpers ----
  const updateAddon = (i: number, patch: Partial<AddonForm>) =>
    setProductAddons((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));

  const addAddon = () => {
    setProductAddons((prev) => [...prev, emptyAddon()]);
    // Expand the new addon; only one card is expanded at a time.
    setExpandedAddon(productAddons.length);
  };

  const removeAddon = (i: number) => {
    setProductAddons((prev) => prev.filter((_, idx) => idx !== i));
    setExpandedAddon((cur) => {
      if (cur === null) return null;
      if (cur === i) return null;
      return cur > i ? cur - 1 : cur;
    });
  };

  const addAddonOption = (ai: number) =>
    setProductAddons((prev) =>
      prev.map((a, idx) => (idx === ai ? { ...a, options: [...a.options, { name: '', price: '' }] } : a))
    );

  const updateAddonOption = (ai: number, oi: number, patch: Partial<AddonOptionForm>) =>
    setProductAddons((prev) =>
      prev.map((a, idx) =>
        idx === ai ? { ...a, options: a.options.map((o, j) => (j === oi ? { ...o, ...patch } : o)) } : a
      )
    );

  const removeAddonOption = (ai: number, oi: number) =>
    setProductAddons((prev) =>
      prev.map((a, idx) => (idx === ai ? { ...a, options: a.options.filter((_, j) => j !== oi) } : a))
    );

  const dropAddon = (dropIndex: number) => {
    const from = dragAddon;
    setDragAddon(null);
    setDragOverAddon(null);
    if (from === null || from === dropIndex) return;
    setProductAddons((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    // Keep the expanded card pointing at the same addon after the move.
    setExpandedAddon((cur) => {
      if (cur === null) return null;
      if (cur === from) return dropIndex;
      if (from < cur && cur <= dropIndex) return cur - 1;
      if (dropIndex <= cur && cur < from) return cur + 1;
      return cur;
    });
  };

  const dropAddonOption = (ai: number, dropIndex: number) => {
    const from = dragOption;
    setDragOption(null);
    setDragOverOption(null);
    if (!from || from.addon !== ai || from.index === dropIndex) return;
    setProductAddons((prev) =>
      prev.map((a, idx) => {
        if (idx !== ai) return a;
        const opts = [...a.options];
        const [moved] = opts.splice(from.index, 1);
        opts.splice(dropIndex, 0, moved);
        return { ...a, options: opts };
      })
    );
  };

  // Pick an image. When editing an existing product it uploads + attaches
  // immediately; for a brand-new product it's held and attached after Save.
  const onImagePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (editingProduct) {
      setUploadingImage(true);
      try {
        const up = await menuApi.uploadFile(file, rid);
        await menuApi.uploadImage(rid, editingProduct.name, up.file_url);
        setProductImageUrl(up.file_url);
        await loadMenu();
      } catch (err) {
        showError(err);
      } finally {
        setUploadingImage(false);
      }
    } else {
      setPendingImageFile(file);
      setProductImageUrl(URL.createObjectURL(file));
    }
  };

  const removeProductImage = async () => {
    if (editingProduct) {
      setUploadingImage(true);
      try {
        await menuApi.uploadImage(rid, editingProduct.name, '');
        setProductImageUrl(null);
        await loadMenu();
      } catch (err) {
        showError(err);
      } finally {
        setUploadingImage(false);
      }
    } else {
      setPendingImageFile(null);
      setProductImageUrl(null);
    }
  };

  const linkExistingProduct = async () => {
    if (!linkProductName || !selectedSection) return;
    setBusy(true);
    try {
      await menuApi.updateProduct(rid, { product_name: linkProductName, menu_section: selectedSection });
      setProductModalOpen(false);
      await loadMenu();
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  const saveProduct = async () => {
    // Strict validation, matching the kitchen-app: all core fields required.
    const missing: string[] = [];
    if (!productForm.food_name.trim()) missing.push('name');
    if (!productForm.food_description.trim()) missing.push('description');
    if (!productForm.ingredients.trim()) missing.push('ingredients');
    const priceNum = parseFloat(productForm.price);
    if (!(priceNum > 0)) missing.push('price');
    const ect = parseInt(productForm.estimated_completion_time, 10);
    if (!(ect > 0)) missing.push('prep time');
    if (missing.length) {
      setToast({ message: `Please provide: ${missing.join(', ')}`, type: 'error' });
      return;
    }
    // Add-ons: sanitize (trim, drop unnamed option rows, drop fully-empty
    // groups), validate, and convert option prices to numbers.
    const addons: ProductAddonInput[] = [];
    for (let i = 0; i < productAddons.length; i++) {
      const a = productAddons[i];
      const title = a.title.trim();
      const opts = a.options
        .map((o) => ({ name: o.name.trim(), price: o.price }))
        .filter((o) => o.name);
      if (!title && opts.length === 0) continue; // empty group — drop silently
      if (!title) {
        setToast({ message: `Add-on ${i + 1} needs a title.`, type: 'error' });
        return;
      }
      if (opts.length === 0) {
        setToast({ message: `Add-on "${title}" needs at least one option with a name.`, type: 'error' });
        return;
      }
      addons.push({
        ...(a.id ? { id: a.id } : {}),
        title,
        selection_type: a.selection_type,
        required: a.required,
        options: opts.map((o) => {
          const n = parseFloat(o.price);
          return { name: o.name, price: Number.isFinite(n) && n > 0 ? n : 0 };
        }),
      });
    }
    setBusy(true);
    try {
      // Always send the full addons array — the backend replaces the whole
      // set on update (an empty array clears all add-ons).
      const base = {
        food_name: productForm.food_name.trim(),
        food_description: productForm.food_description.trim(),
        ingredients: productForm.ingredients.trim(),
        allergens: productForm.allergens.trim(),
        price: productForm.price,
        status: productForm.status,
        food_tags: productForm.food_tags,
        estimated_completion_time: ect,
        addons,
      };
      if (editingProduct) {
        await menuApi.updateProduct(rid, { product_name: editingProduct.name, ...base });
      } else {
        const created = await menuApi.createProduct(rid, { menu_section: selectedSection!, ...base });
        if (pendingImageFile && created?.name) {
          const up = await menuApi.uploadFile(pendingImageFile, rid);
          await menuApi.uploadImage(rid, created.name, up.file_url);
        }
      }
      setProductModalOpen(false);
      await loadMenu();
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };
  const toggleStatus = async (p: FoodProduct) => {
    setBusy(true);
    try {
      await menuApi.updateProduct(rid, {
        product_name: p.name,
        status: p.status === 'Active' ? 'Hidden' : 'Active',
      });
      await loadMenu();
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  // ---- Reorder (drag & drop) ----
  const dropSection = async (dropIndex: number) => {
    const from = dragSection;
    setDragSection(null);
    setDragOverSection(null);
    if (from === null || from === dropIndex) return;
    const next = [...sections];
    const [moved] = next.splice(from, 1);
    next.splice(dropIndex, 0, moved);
    setSections(next); // optimistic
    try {
      await menuApi.reorderSections(rid, next.map((s, i) => ({ name: s.name, order: i + 1 })));
    } catch (e) {
      showError(e);
      await loadMenu();
    }
  };

  const dropProduct = async (dropIndex: number) => {
    const from = dragProduct;
    setDragProduct(null);
    setDragOverProduct(null);
    if (from === null || from === dropIndex) return;
    const list = [...sectionProducts];
    const [moved] = list.splice(from, 1);
    list.splice(dropIndex, 0, moved);
    // Write the reordered section list back into the global products array;
    // sectionProducts (a filter that preserves array order) then reflects it.
    const others = products.filter((p) => p.menu_section !== selectedSection);
    setProducts([...others, ...list]); // optimistic
    try {
      await menuApi.reorderProducts(rid, list.map((p, i) => ({ name: p.name, order: i + 1 })));
    } catch (e) {
      showError(e);
      await loadMenu();
    }
  };

  // ---- Delete ----
  const doDelete = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'section') {
        await menuApi.deleteSection(rid, confirm.id);
        setConfirm(null);
        await loadMenu();
      } else {
        const res = await menuApi.deleteProduct(rid, confirm.id);
        // The backend refuses to delete products with order history; offer to hide.
        if (res && res.success === false && res.has_orders) {
          const target = { id: confirm.id, label: confirm.label };
          setConfirm(null);
          setOrderedProduct(target);
        } else {
          setConfirm(null);
          await loadMenu();
        }
      }
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  const hideOrderedProduct = async () => {
    if (!orderedProduct) return;
    setBusy(true);
    try {
      await menuApi.updateProduct(rid, { product_name: orderedProduct.id, status: 'Hidden' });
      setOrderedProduct(null);
      await loadMenu();
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  // ---- CSV / Excel export & import ----
  const handleExportCsv = async () => {
    setCsvBusy(true);
    try {
      const { csv_data, filename } = await menuApi.exportProducts(rid);
      const blob = new Blob([csv_data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${menuApi.kind}_menu.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      showError(e);
    } finally {
      setCsvBusy(false);
    }
  };

  // For CSV files, validate the mandatory header columns up-front (Excel is left
  // to the backend). Returns the list of missing columns.
  const missingCsvColumns = async (file: File): Promise<string[]> => {
    if (!file.name.toLowerCase().endsWith('.csv')) return [];
    const text = await file.text();
    const firstLine = (text.split(/\r?\n/)[0] || '').toLowerCase();
    const headers = firstLine.split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
    return CSV_REQUIRED_COLUMNS.filter((c) => !headers.includes(c));
  };

  const onCsvPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setCsvBusy(true);
    try {
      const missing = await missingCsvColumns(file);
      if (missing.length) {
        setToast({ message: `CSV is missing mandatory columns: ${missing.join(', ')}`, type: 'error' });
        return;
      }
      const res = await menuApi.importProducts(rid, file);
      await loadMenu();
      const msg = `Imported: ${res.created} created, ${res.updated} updated` +
        (res.errors?.length ? `. ${res.errors.length} row(s) failed.` : '.');
      setToast({ message: msg, type: res.errors?.length ? 'error' : 'success' });
    } catch (err) {
      showError(err);
    } finally {
      setCsvBusy(false);
    }
  };

  // ---- Import from photos (AI, restaurants only) ----
  const onFilesPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same files
    if (!files.length || !menuApi.extractMenu) return;
    setImporting(true);
    try {
      const result = await menuApi.extractMenu(rid, files);
      const review: ReviewSection[] = (result.sections || []).map((s) => ({
        section_name: s.section_name || '',
        existing_section: '',
        products: (s.products || []).map((p) => ({
          food_name: p.name || '',
          ingredients: p.ingredients || p.description || '',
          price: p.price || '',
        })),
      }));
      if (!review.length || review.every((s) => s.products.length === 0)) {
        setToast({ message: 'No menu items could be read from those files.', type: 'error' });
      } else {
        setReviewSections(review);
      }
    } catch (err) {
      showError(err);
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!reviewSections || !menuApi.saveExtractedMenu) return;
    const payload: SaveMenuSection[] = reviewSections
      .map((s) => ({
        section_name: s.existing_section ? undefined : (s.section_name.trim() || 'Menu'),
        existing_section: s.existing_section || undefined,
        products: s.products
          .filter((p) => p.food_name.trim())
          .map((p) => ({
            food_name: p.food_name.trim(),
            ingredients: p.ingredients.trim() || undefined,
            price: p.price,
          })),
      }))
      .filter((s) => s.products.length > 0);

    if (!payload.length) {
      setToast({ message: 'Nothing to import — add at least one product.', type: 'error' });
      return;
    }
    setSavingImport(true);
    try {
      const res = await menuApi.saveExtractedMenu(rid, payload);
      setReviewSections(null);
      await loadMenu();
      const msg = `Imported ${res.created_products} item(s) into ${res.created_sections} new section(s) as Hidden.` +
        (res.errors?.length ? ` ${res.errors.length} item(s) failed.` : '');
      setToast({ message: msg, type: res.errors?.length ? 'error' : 'success' });
    } catch (e) {
      showError(e);
    } finally {
      setSavingImport(false);
    }
  };

  // ---- review modal editing helpers ----
  const updateReviewSection = (i: number, patch: Partial<ReviewSection>) =>
    setReviewSections((prev) => prev && prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const updateReviewProduct = (si: number, pi: number, patch: Partial<ReviewProduct>) =>
    setReviewSections((prev) =>
      prev &&
      prev.map((s, idx) =>
        idx === si ? { ...s, products: s.products.map((p, j) => (j === pi ? { ...p, ...patch } : p)) } : s
      )
    );
  const removeReviewProduct = (si: number, pi: number) =>
    setReviewSections((prev) =>
      prev && prev.map((s, idx) => (idx === si ? { ...s, products: s.products.filter((_, j) => j !== pi) } : s))
    );
  const removeReviewSection = (si: number) =>
    setReviewSections((prev) => prev && prev.filter((_, idx) => idx !== si));

  const reviewTotal = reviewSections?.reduce((n, s) => n + s.products.filter((p) => p.food_name.trim()).length, 0) || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading menu…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(menuApi.backPath)}
            className="text-gray-500 hover:text-gray-800"
            title="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Menu</h1>
            <p className="text-sm text-gray-500">{entityName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* hidden inputs */}
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,.xlsx"
            className="hidden"
            onChange={onCsvPicked}
          />
          {menuApi.supportsAiImport && (
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.pdf,image/*,application/pdf"
              multiple
              className="hidden"
              onChange={onFilesPicked}
            />
          )}
          <button
            onClick={handleExportCsv}
            disabled={csvBusy}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            title="Export menu to CSV"
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </button>
          <button
            onClick={() => csvInputRef.current?.click()}
            disabled={isReadOnly || csvBusy}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            title="Import menu from CSV / Excel"
          >
            {csvBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
            Import CSV
          </button>
          {menuApi.supportsAiImport && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isReadOnly || importing}
              className="inline-flex items-center px-4 py-2 bg-alps-primary text-white rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {importing ? 'Reading menu…' : 'Import from photos / PDF'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sections */}
        <div className="lg:col-span-4">
          <Card title="Sections">
            <div className="space-y-1">
              {sections.length === 0 && (
                <p className="text-sm text-gray-400 py-2">No sections yet.</p>
              )}
              {sections.map((s, index) => (
                <div
                  key={s.name}
                  draggable={!isReadOnly}
                  onDragStart={() => setDragSection(index)}
                  onDragEnd={() => { setDragSection(null); setDragOverSection(null); }}
                  onDragOver={(e) => { e.preventDefault(); if (dragSection !== null && dragSection !== index) setDragOverSection(index); }}
                  onDragLeave={() => setDragOverSection((cur) => (cur === index ? null : cur))}
                  onDrop={(e) => { e.preventDefault(); dropSection(index); }}
                  className={`group flex items-center justify-between rounded-md px-2 py-2 cursor-pointer ${
                    dragOverSection === index && dragSection !== index ? 'ring-2 ring-inset ring-alps-primary' : ''
                  } ${dragSection === index ? 'opacity-50' : ''} ${
                    selectedSection === s.name ? 'bg-alps-primary/10 text-alps-primary' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedSection(s.name)}
                >
                  <span className="flex items-center min-w-0">
                    <GripVertical className="h-4 w-4 text-gray-300 mr-1 flex-shrink-0 cursor-grab" />
                    <span className="text-sm font-medium truncate">
                      {s.section_name}
                      <span className="ml-2 text-xs text-gray-400">{productCount[s.name] || 0}</span>
                    </span>
                  </span>
                  <span className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); openEditSection(s); }} disabled={isReadOnly} className="text-gray-400 hover:text-indigo-600 disabled:opacity-40" title="Rename">
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setConfirm({ kind: 'section', id: s.name, label: s.section_name }); }} disabled={isReadOnly} className="text-gray-400 hover:text-red-600 disabled:opacity-40" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </span>
                </div>
              ))}
            </div>
            <button
              onClick={openAddSection}
              disabled={isReadOnly}
              className="mt-3 inline-flex items-center text-sm text-alps-primary hover:opacity-80 disabled:opacity-50"
            >
              <Plus className="h-4 w-4 mr-1" /> Add section
            </button>
          </Card>
        </div>

        {/* Products */}
        <div className="lg:col-span-8">
          <Card
            title={
              <div className="flex items-center justify-between">
                <span>{selectedSection ? sections.find((s) => s.name === selectedSection)?.section_name : 'Products'}</span>
                {selectedSection && (
                  <button
                    onClick={openAddProduct}
                    disabled={isReadOnly}
                    className="inline-flex items-center text-sm text-alps-primary hover:opacity-80 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add product
                  </button>
                )}
              </div>
            }
          >
            {!selectedSection ? (
              <p className="text-sm text-gray-400 py-4">Select or create a section to manage its products.</p>
            ) : sectionProducts.length === 0 ? (
              <p className="text-sm text-gray-400 py-4">No products in this section yet.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {sectionProducts.map((p, index) => (
                  <div
                    key={p.name}
                    draggable={!isReadOnly}
                    onDragStart={() => setDragProduct(index)}
                    onDragEnd={() => { setDragProduct(null); setDragOverProduct(null); }}
                    onDragOver={(e) => { e.preventDefault(); if (dragProduct !== null && dragProduct !== index) setDragOverProduct(index); }}
                    onDragLeave={() => setDragOverProduct((cur) => (cur === index ? null : cur))}
                    onDrop={(e) => { e.preventDefault(); dropProduct(index); }}
                    className={`flex items-start justify-between py-3 px-1 -mx-1 rounded ${
                      dragOverProduct === index && dragProduct !== index ? 'ring-2 ring-inset ring-alps-primary' : ''
                    } ${dragProduct === index ? 'opacity-50' : ''}`}
                  >
                    <div className="min-w-0 pr-4 flex items-start gap-2">
                      <GripVertical className="h-4 w-4 text-gray-300 mt-0.5 flex-shrink-0 cursor-grab" />
                      {p.image && (
                        <img src={getImageUrl(p.image)} alt="" className="h-10 w-10 rounded-md object-cover border border-gray-200 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900 truncate">{p.food_name}</span>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${
                            p.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {p.status}
                        </span>
                      </div>
                      {(p.ingredients || p.food_description) && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{p.ingredients || p.food_description}</p>
                      )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 flex-shrink-0">
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">€{Number(p.price || 0).toFixed(2)}</span>
                      <button onClick={() => toggleStatus(p)} disabled={isReadOnly || busy} className="text-gray-400 hover:text-gray-700 disabled:opacity-50" title={p.status === 'Active' ? 'Hide' : 'Show'}>
                        {p.status === 'Active' ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button onClick={() => openEditProduct(p)} disabled={isReadOnly} className="text-gray-400 hover:text-indigo-600 disabled:opacity-50" title="Edit">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => setConfirm({ kind: 'product', id: p.name, label: p.food_name })} disabled={isReadOnly} className="text-gray-400 hover:text-red-600 disabled:opacity-50" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Section modal */}
      {sectionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSectionModalOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{editingSection ? 'Rename section' : 'Add section'}</h3>
            <input
              autoFocus
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveSection()}
              placeholder="Section name"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-alps-primary focus:border-alps-primary"
            />
            <div className="flex justify-end space-x-2">
              <button onClick={() => setSectionModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={saveSection} disabled={busy || !sectionName.trim()} className="px-4 py-2 rounded-md text-sm font-medium text-white bg-alps-primary hover:opacity-90 disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Product modal */}
      {productModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setProductModalOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900">{editingProduct ? 'Edit product' : 'Add product'}</h3>
            {!editingProduct && linkableOptions.length > 0 && (
              <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <label className="block text-xs font-medium text-gray-600 mb-1">Add an existing product to this section</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Select
                      options={linkableOptions}
                      value={linkableOptions.find((o) => o.value === linkProductName) || null}
                      onChange={(v) => setLinkProductName(v?.value ?? null)}
                      classNamePrefix="rs"
                      placeholder="Select an existing product…"
                      isClearable
                    />
                  </div>
                  <button
                    type="button"
                    onClick={linkExistingProduct}
                    disabled={!linkProductName || busy}
                    className="px-3 py-2 rounded-md text-sm font-medium text-white bg-alps-primary hover:opacity-90 disabled:opacity-50"
                  >
                    Link
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">…or fill in the form below to create a new product.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Image</label>
                <div className="flex items-center gap-3">
                  {productImageUrl ? (
                    <img src={getImageUrl(productImageUrl)} alt="" className="h-16 w-16 rounded-md object-cover border border-gray-200" />
                  ) : (
                    <div className="h-16 w-16 rounded-md border border-dashed border-gray-300 flex items-center justify-center text-gray-300">
                      <ImagePlus className="h-6 w-6" />
                    </div>
                  )}
                  <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={onImagePicked} />
                  <button type="button" onClick={() => imageInputRef.current?.click()} disabled={uploadingImage} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    {uploadingImage ? 'Uploading…' : productImageUrl ? 'Replace' : 'Upload image'}
                  </button>
                  {productImageUrl && (
                    <button type="button" onClick={removeProductImage} disabled={uploadingImage} className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50">Remove</button>
                  )}
                </div>
                {!editingProduct && pendingImageFile && (
                  <p className="text-xs text-gray-400 mt-1">Image will be attached when you save the product.</p>
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                <input value={productForm.food_name} onChange={(e) => setProductForm({ ...productForm, food_name: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Price (€) *</label>
                <input type="number" step="0.01" min="0" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select value={productForm.status} onChange={(e) => setProductForm({ ...productForm, status: e.target.value as 'Active' | 'Hidden' })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                  <option value="Active">Active</option>
                  <option value="Hidden">Hidden</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Ingredients *</label>
                <textarea rows={2} value={productForm.ingredients} onChange={(e) => setProductForm({ ...productForm, ingredients: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Description *</label>
                <textarea rows={2} value={productForm.food_description} onChange={(e) => setProductForm({ ...productForm, food_description: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Allergens</label>
                <input value={productForm.allergens} onChange={(e) => setProductForm({ ...productForm, allergens: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Prep time (min) *</label>
                <input type="number" min="1" value={productForm.estimated_completion_time} onChange={(e) => setProductForm({ ...productForm, estimated_completion_time: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Tags (max {MAX_TAGS})</label>
                <Select
                  isMulti
                  options={productForm.food_tags.length >= MAX_TAGS ? [] : tagOptions}
                  value={productForm.food_tags.map((t) => ({ value: t, label: t }))}
                  onChange={(vals) => {
                    const arr = vals.map((v) => v.value).slice(0, MAX_TAGS);
                    setProductForm({ ...productForm, food_tags: arr });
                  }}
                  noOptionsMessage={() => (productForm.food_tags.length >= MAX_TAGS ? MAX_TAGS_MESSAGE : 'No tags found')}
                  classNamePrefix="rs"
                  placeholder="Select tags…"
                />
                {productForm.food_tags.length >= MAX_TAGS && (
                  <p className="text-xs text-gray-400 mt-1">{MAX_TAGS_MESSAGE}</p>
                )}
              </div>
              {/* Add-ons (option groups) */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Add-ons</label>
                <div className="space-y-3">
                  {productAddons.map((addon, ai) => (
                    <div
                      key={ai}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (dragAddon !== null && dragAddon !== ai) setDragOverAddon(ai);
                      }}
                      onDragLeave={() => setDragOverAddon((cur) => (cur === ai ? null : cur))}
                      onDrop={(e) => { e.preventDefault(); dropAddon(ai); }}
                      className={`border border-gray-200 rounded-lg px-2 py-4 ${
                        dragOverAddon === ai && dragAddon !== ai ? 'ring-2 ring-inset ring-alps-primary' : ''
                      } ${dragAddon === ai ? 'opacity-50' : ''}`}
                    >
                      {/* Header row: click toggles; expanding one collapses the others */}
                      <div
                        draggable={!isReadOnly}
                        onDragStart={() => setDragAddon(ai)}
                        onDragEnd={() => { setDragAddon(null); setDragOverAddon(null); }}
                        onClick={() => setExpandedAddon((cur) => (cur === ai ? null : ai))}
                        className="flex items-center justify-between gap-2 cursor-pointer"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0 cursor-grab" />
                          <span className="bg-alps-primary/10 text-alps-primary text-xs font-semibold rounded-full flex items-center justify-center h-6 w-6 flex-shrink-0">
                            {ai + 1}
                          </span>
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {addon.title.trim() || `Add-on ${ai + 1}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {addon.required ? (
                            <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded">Required</span>
                          ) : (
                            <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded">Optional</span>
                          )}
                          <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded">
                            {addon.selection_type === 'single' ? 'Single select' : 'Multi select'}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setExpandedAddon((cur) => (cur === ai ? null : ai)); }}
                            className="text-gray-400 hover:text-gray-700"
                            title={expandedAddon === ai ? 'Collapse' : 'Expand'}
                          >
                            {expandedAddon === ai ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      {expandedAddon === ai && (
                        <div className="border-t border-gray-200 mt-4 pt-4 space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                            <input
                              value={addon.title}
                              disabled={isReadOnly}
                              onChange={(e) => updateAddon(ai, { title: e.target.value })}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="inline-flex h-10 items-center gap-1 rounded-md bg-gray-100 p-1">
                              <button
                                type="button"
                                disabled={isReadOnly}
                                onClick={() => updateAddon(ai, { required: true })}
                                className={`px-3 py-1.5 rounded text-sm font-medium ${
                                  addon.required ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                                }`}
                              >
                                Required
                              </button>
                              <button
                                type="button"
                                disabled={isReadOnly}
                                onClick={() => updateAddon(ai, { required: false })}
                                className={`px-3 py-1.5 rounded text-sm font-medium ${
                                  !addon.required ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                                }`}
                              >
                                Optional
                              </button>
                            </div>
                            <div className="inline-flex h-10 items-center gap-1 rounded-md bg-gray-100 p-1">
                              <button
                                type="button"
                                disabled={isReadOnly}
                                onClick={() => updateAddon(ai, { selection_type: 'single' })}
                                className={`px-3 py-1.5 rounded text-sm font-medium ${
                                  addon.selection_type === 'single' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                                }`}
                              >
                                Single select
                              </button>
                              <button
                                type="button"
                                disabled={isReadOnly}
                                onClick={() => updateAddon(ai, { selection_type: 'multi' })}
                                className={`px-3 py-1.5 rounded text-sm font-medium ${
                                  addon.selection_type === 'multi' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                                }`}
                              >
                                Multi select
                              </button>
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="w-4 flex-shrink-0" />
                              <span className="flex-1 block text-xs font-medium text-gray-600">Options</span>
                              <span className="w-24 block text-xs font-medium text-gray-600">Prices</span>
                              <span className="w-5 flex-shrink-0" />
                            </div>
                            <div className="space-y-2">
                              {addon.options.map((opt, oi) => (
                                <div
                                  key={oi}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (dragOption && dragOption.addon === ai && dragOption.index !== oi) {
                                      setDragOverOption({ addon: ai, index: oi });
                                    }
                                  }}
                                  onDragLeave={() =>
                                    setDragOverOption((cur) => (cur && cur.addon === ai && cur.index === oi ? null : cur))
                                  }
                                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); dropAddonOption(ai, oi); }}
                                  className={`flex items-center gap-2 rounded ${
                                    dragOverOption && dragOverOption.addon === ai && dragOverOption.index === oi &&
                                    !(dragOption && dragOption.addon === ai && dragOption.index === oi)
                                      ? 'ring-2 ring-inset ring-alps-primary'
                                      : ''
                                  } ${dragOption && dragOption.addon === ai && dragOption.index === oi ? 'opacity-50' : ''}`}
                                >
                                  <span
                                    draggable={!isReadOnly}
                                    onDragStart={(e) => { e.stopPropagation(); setDragOption({ addon: ai, index: oi }); }}
                                    onDragEnd={(e) => { e.stopPropagation(); setDragOption(null); setDragOverOption(null); }}
                                    className="flex-shrink-0 cursor-grab"
                                  >
                                    <GripVertical className="h-4 w-4 text-gray-300" />
                                  </span>
                                  <input
                                    value={opt.name}
                                    disabled={isReadOnly}
                                    onChange={(e) => updateAddonOption(ai, oi, { name: e.target.value })}
                                    placeholder="Option name"
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="€0.00"
                                    value={opt.price}
                                    disabled={isReadOnly}
                                    onChange={(e) => updateAddonOption(ai, oi, { price: e.target.value })}
                                    className="w-24 border border-gray-300 rounded-md px-3 py-2 text-sm"
                                  />
                                  <button
                                    type="button"
                                    disabled={isReadOnly}
                                    onClick={() => removeAddonOption(ai, oi)}
                                    className="text-gray-400 hover:text-red-500 disabled:opacity-40"
                                    title="Remove option"
                                  >
                                    <X className="h-5 w-5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-gray-400 mt-2">Option price is a surcharge added to the product's base price.</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={isReadOnly}
                              onClick={() => addAddonOption(ai)}
                              className="inline-flex items-center gap-1.5 h-9 px-4 border border-gray-300 rounded-md bg-white text-sm font-medium text-alps-primary hover:bg-gray-50 disabled:opacity-50"
                            >
                              <Plus className="h-5 w-5" /> Add Option
                            </button>
                            <button
                              type="button"
                              disabled={isReadOnly}
                              onClick={() => removeAddon(ai)}
                              className="inline-flex items-center gap-1.5 h-9 px-4 border border-gray-300 rounded-md bg-white text-sm font-medium text-red-600 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Remove section
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    disabled={isReadOnly}
                    onClick={addAddon}
                    className="inline-flex items-center gap-1.5 h-9 px-4 border border-gray-300 rounded-md bg-white text-sm font-medium text-alps-primary hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Plus className="h-5 w-5" /> Add Add-on
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button onClick={() => setProductModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={saveProduct} disabled={busy} className="px-4 py-2 rounded-md text-sm font-medium text-white bg-alps-primary hover:opacity-90 disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Import review modal */}
      {reviewSections && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" />
          <div className="relative bg-white rounded-lg shadow-lg max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Review imported menu</h3>
                <p className="text-xs text-gray-500">{reviewTotal} item(s). They will be created as <span className="font-medium">Hidden</span> until you activate them.</p>
              </div>
              <button onClick={() => setReviewSections(null)} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-4 overflow-y-auto space-y-5">
              {reviewSections.map((s, si) => (
                <div key={si} className="border border-gray-200 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      value={s.section_name}
                      disabled={!!s.existing_section}
                      onChange={(e) => updateReviewSection(si, { section_name: e.target.value })}
                      placeholder="Section name"
                      className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm font-medium disabled:bg-gray-100"
                    />
                    <select
                      value={s.existing_section}
                      onChange={(e) => updateReviewSection(si, { existing_section: e.target.value })}
                      className="border border-gray-300 rounded-md px-2 py-1 text-xs text-gray-600"
                      title="Create a new section or add into an existing one"
                    >
                      <option value="">Create new</option>
                      {sections.map((sec) => (
                        <option key={sec.name} value={sec.name}>→ {sec.section_name}</option>
                      ))}
                    </select>
                    <button onClick={() => removeReviewSection(si)} className="text-gray-400 hover:text-red-600" title="Remove section"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="space-y-2">
                    {s.products.map((p, pi) => (
                      <div key={pi} className="flex items-center gap-2">
                        <input value={p.food_name} onChange={(e) => updateReviewProduct(si, pi, { food_name: e.target.value })} placeholder="Name" className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm" />
                        <input value={p.ingredients} onChange={(e) => updateReviewProduct(si, pi, { ingredients: e.target.value })} placeholder="Ingredients" className="flex-[2] border border-gray-300 rounded-md px-2 py-1 text-sm" />
                        <input value={p.price} onChange={(e) => updateReviewProduct(si, pi, { price: e.target.value })} placeholder="Price" className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm" />
                        <button onClick={() => removeReviewProduct(si, pi)} className="text-gray-400 hover:text-red-600" title="Remove"><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                    {s.products.length === 0 && <p className="text-xs text-gray-400">No products in this section.</p>}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-2">
              <button onClick={() => setReviewSections(null)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={confirmImport} disabled={savingImport || reviewTotal === 0} className="px-4 py-2 rounded-md text-sm font-medium text-white bg-alps-primary hover:opacity-90 disabled:opacity-50">
                {savingImport ? 'Importing…' : `Add ${reviewTotal} item(s) to menu`}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.kind === 'section' ? 'Delete section' : 'Delete product'}
        message={
          confirm?.kind === 'section'
            ? `Delete section "${confirm?.label}"? Products in it will be unlinked.`
            : `Delete product "${confirm?.label}"?`
        }
        confirmLabel="Delete"
        loading={busy}
        onConfirm={doDelete}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={!!orderedProduct}
        title="Product has orders"
        message={`"${orderedProduct?.label}" has order history and can't be deleted. Hide it instead so customers no longer see it?`}
        confirmLabel="Hide product"
        loading={busy}
        onConfirm={hideOrderedProduct}
        onCancel={() => setOrderedProduct(null)}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default MenuEditor;
